import { Suspense, lazy, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@iconify/react";

import type {
  PeriodKind,
  PeriodSummary,
  PrescriptionBump,
} from "../../../shared/src";
import {
  getActiveWorkout,
  getPeriodSummaries,
  getPrsByPeriod,
  getVolumeByMuscleGroup,
  logout,
} from "../api-client";
import { BumpToast } from "../components/BumpToast";
import { ChartFallback } from "../components/ChartFallback";
import { continuousPeriodStarts } from "../components/period-buckets";
import { DashboardTile } from "../components/DashboardTile";
import { VersionFooter } from "../components/VersionFooter";
import { SESSION_QUERY_KEY } from "../hooks/useSession";

const RecentTrainingChart = lazy(() =>
  import("../components/RecentTrainingChart").then((m) => ({
    default: m.RecentTrainingChart,
  })),
);

const MuscleGroupVolumeWidget = lazy(() =>
  import("../components/MuscleGroupVolumeWidget").then((m) => ({
    default: m.MuscleGroupVolumeWidget,
  })),
);

const PrsByPeriodChart = lazy(() =>
  import("../components/PrsByPeriodChart").then((m) => ({
    default: m.PrsByPeriodChart,
  })),
);

const TODAY_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
};

function readBumpsFromLocationState(state: unknown): PrescriptionBump[] {
  if (typeof state !== "object" || state === null) return [];
  const bumps = (state as { bumps?: unknown }).bumps;
  if (!Array.isArray(bumps)) return [];
  return bumps as PrescriptionBump[];
}

export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<PeriodKind>("weekly");
  const [bumps, setBumps] = useState<PrescriptionBump[]>(() =>
    readBumpsFromLocationState(location.state),
  );

  useEffect(() => {
    if (bumps.length > 0 && location.state) {
      // Clear router state so a refresh doesn't re-show the toast.
      window.history.replaceState({}, "");
    }
    // Run once on mount only — we want the snapshot of state at navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeWorkoutQuery = useQuery({
    queryKey: ["activeWorkout"] as const,
    queryFn: () => getActiveWorkout(),
  });

  const summariesQuery = useQuery({
    queryKey: ["periodSummaries", period] as const,
    queryFn: () => getPeriodSummaries({ period, limit: 8 }),
  });

  const muscleGroupQuery = useQuery({
    queryKey: ["volumeByMuscleGroup", period] as const,
    queryFn: () => getVolumeByMuscleGroup({ period, limit: 8 }),
  });

  const prsByPeriodQuery = useQuery({
    queryKey: ["prsByPeriod", period] as const,
    queryFn: () => getPrsByPeriod({ period, limit: 8 }),
  });

  async function onLogout() {
    try {
      await logout();
    } catch {
      // network or auth errors still proceed to /login — local cookie is gone
    } finally {
      queryClient.setQueryData(SESSION_QUERY_KEY, { authenticated: false });
      navigate("/login", { replace: true });
    }
  }

  const active = activeWorkoutQuery.data ?? null;
  const summaries = summariesQuery.data ?? [];
  const currentBucket = summaries[summaries.length - 1] ?? null;

  return (
    <main className="min-h-dvh p-4 space-y-5 sm:p-6 sm:space-y-6">
      {bumps.length > 0 && (
        <BumpToast bumps={bumps} onDismiss={() => setBumps([])} />
      )}
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Today
          </p>
          <h1 className="text-xl font-semibold sm:text-2xl">
            {new Date().toLocaleDateString(undefined, TODAY_OPTIONS)}
          </h1>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
        >
          Log out
        </button>
      </header>

      <StatsRow current={currentBucket} period={period} />

      {active && (
        <section
          role="region"
          aria-label="active workout"
          className="rounded-2xl border border-orange-600 bg-orange-950/30 p-4 space-y-2"
        >
          <p className="text-sm font-medium text-orange-200">
            Workout in progress
          </p>
          <p className="text-xs text-orange-300/80">
            Started {formatStartedAt(active.started_at)}
            {active.training_day_id === null ? " · ad-hoc" : ""}
          </p>
          <Link
            to={`/workouts/${active.id}`}
            className="inline-flex rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500"
          >
            Resume workout →
          </Link>
        </section>
      )}

      <section
        role="region"
        aria-label="training summary"
        className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3"
      >
        <header className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-slate-200">
              Recent training
            </h2>
            <p className="text-xs text-slate-500 tabular-nums">
              {formatAverageLabel(summaries, period)}
            </p>
          </div>
          <div
            role="tablist"
            aria-label="period"
            className="inline-flex rounded-md border border-slate-700 text-xs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={period === "weekly"}
              onClick={() => setPeriod("weekly")}
              className={`px-3 py-1 rounded-l-md ${
                period === "weekly"
                  ? "bg-slate-700 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Weekly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={period === "monthly"}
              onClick={() => setPeriod("monthly")}
              className={`px-3 py-1 rounded-r-md ${
                period === "monthly"
                  ? "bg-slate-700 text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Monthly
            </button>
          </div>
        </header>
        {summariesQuery.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : summaries.length === 0 ? (
          <p className="text-xs text-slate-500">
            No finished workouts yet — your weekly and monthly totals will land
            here.
          </p>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <RecentTrainingChart rows={summaries} period={period} />
          </Suspense>
        )}
      </section>

      <section
        role="region"
        aria-label="volume by muscle group"
        className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3"
      >
        <header className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-slate-200">
            Volume by muscle group
          </h2>
        </header>
        {muscleGroupQuery.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <MuscleGroupVolumeWidget
              rows={muscleGroupQuery.data ?? []}
              period={period}
            />
          </Suspense>
        )}
      </section>

      <section
        role="region"
        aria-label="new PRs"
        className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3"
      >
        <header className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-medium text-slate-200">New PRs</h2>
            <p className="text-xs text-slate-500 tabular-nums">
              {formatPrTotalLabel(prsByPeriodQuery.data ?? [])}
            </p>
          </div>
        </header>
        {prsByPeriodQuery.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (prsByPeriodQuery.data ?? []).length === 0 ? (
          <p className="text-xs text-slate-500">
            No personal records yet — every new top set will show up here.
          </p>
        ) : (
          <Suspense fallback={<ChartFallback />}>
            <PrsByPeriodChart
              rows={prsByPeriodQuery.data ?? []}
              period={period}
            />
          </Suspense>
        )}
      </section>

      <nav
        aria-label="primary"
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        {!active && (
          <DashboardTile
            to="/workouts"
            icon="fluent-emoji:flexed-biceps"
            label="Workouts"
            subtext="Start training"
          />
        )}
        <DashboardTile
          to="/programs"
          icon="fluent-emoji:clipboard"
          label="Programs"
        />
        <DashboardTile
          to="/exercises"
          icon="fluent-emoji:weight-lifter"
          label="Exercises"
        />
        <DashboardTile
          to="/bodyweight"
          icon="fluent-emoji:balance-scale"
          label="Bodyweight"
        />
      </nav>

      <VersionFooter />
    </main>
  );
}

function StatsRow({
  current,
  period,
}: {
  current: PeriodSummary | null;
  period: PeriodKind;
}) {
  const label = period === "weekly" ? "This week" : "This month";
  const sessions = current?.sessions ?? 0;
  const volume = current?.total_volume_kg ?? 0;
  const topE1rm = current?.top_e1rm_kg ?? 0;
  return (
    <section
      role="region"
      aria-label={`stats ${label}`}
      className="grid grid-cols-3 gap-3"
    >
      <StatCard
        tone="bg-orange-500"
        icon="fluent-emoji:fire"
        label="Sessions"
        value={String(sessions)}
        subtext={label}
      />
      <StatCard
        tone="bg-orange-600"
        icon="fluent-emoji:weight-lifter"
        label="Volume"
        value={formatVolume(volume)}
        subtext="kg"
      />
      <StatCard
        tone="bg-orange-700"
        icon="fluent-emoji:trophy"
        label="Top e1RM"
        value={topE1rm > 0 ? Math.round(topE1rm).toString() : "—"}
        subtext="kg"
      />
    </section>
  );
}

function StatCard({
  tone,
  icon,
  label,
  value,
  subtext,
}: {
  tone: string;
  icon: string;
  label: string;
  value: string;
  subtext: string;
}) {
  return (
    <div
      className={`rounded-2xl p-3 text-white shadow-sm ${tone} flex flex-col items-center text-center`}
    >
      <Icon icon={icon} className="h-7 w-7 sm:h-9 sm:w-9" aria-hidden="true" />
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-none sm:text-3xl">
        {value}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wide opacity-80 sm:text-xs">
        {label}
      </div>
      <div className="text-[10px] opacity-70 sm:text-xs">{subtext}</div>
    </div>
  );
}

function formatVolume(kg: number): string {
  if (kg >= 1000) {
    return `${(kg / 1000).toFixed(1)}k`;
  }
  return Math.round(kg).toString();
}

function formatAverageLabel(rows: PeriodSummary[], period: PeriodKind): string {
  if (rows.length === 0) return "";
  // Average over the same calendar buckets the chart draws, so rest weeks
  // or months count as zero rather than being skipped.
  const buckets = new Set(
    continuousPeriodStarts(
      rows.map((r) => r.period_start),
      period,
    ),
  );
  const total = rows
    .filter((r) => buckets.has(r.period_start))
    .reduce((s, r) => s + r.total_volume_kg, 0);
  const avg = total / buckets.size;
  const label = period === "weekly" ? "Weekly average" : "Monthly average";
  return `${label} ${formatVolume(avg)} kg`;
}

function formatPrTotalLabel(rows: { count: number }[]): string {
  if (rows.length === 0) return "";
  const total = rows.reduce((s, r) => s + r.count, 0);
  return `${total} ${total === 1 ? "PR" : "PRs"} in shown window`;
}

function formatStartedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleString();
}
